export const getPaginationRange = (
  currentPage: number,
  totalPages: number,
  maxVisible: number = 5,
): number[] => {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const halfVisible = Math.floor(maxVisible / 2);
  let startPage = Math.max(1, currentPage - halfVisible);
  const endPage = Math.min(totalPages, startPage + maxVisible - 1);

  if (endPage - startPage + 1 < maxVisible) {
    startPage = Math.max(1, endPage - maxVisible + 1);
  }

  return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
};

type PaginationConfig = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  maxVisible?: number;
  loading?: boolean;
};

type PaginationData = {
  pages: number[];
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  canGoPrevious: boolean;
  canGoNext: boolean;
  goToPrevious: () => void;
  goToNext: () => void;
  loading: boolean;
};

export const getPaginationData = (config: PaginationConfig): PaginationData => {
  const { currentPage, totalPages, onPageChange, maxVisible = 5, loading } = config;

  const pages = getPaginationRange(currentPage, totalPages, maxVisible);
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  const goToPrevious = () => {
    if (canGoPrevious) {
      onPageChange(currentPage - 1);
    }
  };

  const goToNext = () => {
    if (canGoNext) {
      onPageChange(currentPage + 1);
    }
  };

  return {
    pages,
    currentPage,
    totalPages,
    onPageChange,
    canGoPrevious,
    canGoNext,
    goToPrevious,
    goToNext,
    loading: !!loading,
  };
};
